import React, { useContext, useEffect } from 'react';
import { Form, Input, InputNumber, Select, DatePicker, Switch, Button, Card, Row, Col, Space, message } from 'antd';
import { SaveOutlined, LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';

const { Option } = Select;
const { TextArea } = Input;

function CouponCreate() {
  const navigate = useNavigate();
  const { setHeaderComponent } = useContext(LayoutContext);
  const [form] = Form.useForm();
  const { doFetch: createCouponFetch } = useFetch();

  useEffect(() => {
    setHeaderComponent('Create New Coupon');

    return () => {
      setHeaderComponent(null);
    };
  }, [setHeaderComponent]);

  const handleSubmit = async (values) => {
    const formData = {
      code: values.code,
      discount_type: values.discount_type,
      discount_value: values.discount_value,
      currency: values.currency || 'USD',
      description: values.description,
      expires_at: values.valid_until ? values.valid_until.format('YYYY-MM-DD') : null,
      usage_limit: values.max_usage || null,
      minimum_order_amount: values.min_order_amount || null,
      is_active: values.is_active !== undefined ? values.is_active : true,
    };

    createCouponFetch({
      url: URLS.createCoupon(),
      method: 'POST',
      params: formData,
      onSuccess: () => {
        message.success('Coupon created successfully!');
        navigate('/coupons');
      },
      onError: (error) => {
        message.error('Failed to create coupon. Please try again.');
        console.error('Error creating coupon:', error);
      },
      showSuccessNotification: true,
      successMessage: 'Coupon created successfully',
    });
  };

  const handleCancel = () => {
    navigate('/coupons');
  };

  return (
    <Row justify="center">
      <Col xs={24} sm={22} md={20} lg={16} xl={12}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              discount_type: 'percentage',
              is_active: true,
            }}
          >
            <Form.Item
              name="code"
              label="Coupon Code"
              rules={[
                { required: true, message: 'Please enter a coupon code' },
                { pattern: /^[A-Z0-9_-]+$/, message: 'Code must contain only uppercase letters, numbers, hyphens, and underscores' },
              ]}
            >
              <Input
                placeholder="e.g., SAVE20"
                onChange={(e) => {
                  const upperValue = e.target.value.toUpperCase();
                  form.setFieldsValue({ code: upperValue });
                }}
              />
            </Form.Item>

            <Form.Item
              name="discount_type"
              label="Discount Type"
              rules={[{ required: true, message: 'Please select a discount type' }]}
            >
              <Select>
                <Option value="percentage">Percentage</Option>
                <Option value="fixed">Fixed Amount</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="discount_value"
              label="Discount Value"
              rules={[
                { required: true, message: 'Please enter discount value' },
                { type: 'number', min: 0, message: 'Discount value must be positive' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter discount value"
                min={0}
                formatter={(value) => {
                  if (!value) return '';
                  const discountType = form.getFieldValue('discount_type');
                  return discountType === 'percentage' ? `${value}%` : value;
                }}
              />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.discount_type !== currentValues.discount_type}
            >
              {({ getFieldValue }) =>
                getFieldValue('discount_type') === 'fixed' ? (
                  <Form.Item
                    name="currency"
                    label="Currency (for fixed amount)"
                    initialValue="IQD"
                  >
                    <Select>
                      <Option value="USD">USD</Option>
                      <Option value="EUR">EUR</Option>
                      <Option value="GBP">GBP</Option>
                      <Option value="IQD">IQD</Option>
                    </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea rows={3} placeholder="Optional description for this coupon" />
            </Form.Item>

            <Form.Item
              name="valid_until"
              label="Expires At"
              rules={[{ required: true, message: 'Please select expiration date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Select expiration date"
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="max_usage"
                  label="Maximum Usage"
                  tooltip="Leave empty for unlimited usage"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Unlimited"
                    min={1}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="min_order_amount"
                  label="Minimum Order Amount"
                  tooltip="Minimum order amount required to use this coupon"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="No minimum"
                    min={0}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="is_active"
              label="Active Status"
              valuePropName="checked"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} size="large">
                  Create Coupon
                </Button>
                <Button icon={<LeftOutlined />} onClick={handleCancel} size="large">
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
}

export default CouponCreate;

